import { useMemo } from 'react'
import type { RawContact, RawMessage } from '../components/types'
import { CHART_COLORS } from '../utils/constants'

export interface VarValueBreakdownItem {
  value: string
  count: number
  color: string
}

export type PinnedVarChartType = 'bar' | 'pie' | 'number'
export type PinnedVarSortOrder = 'count_desc' | 'count_asc' | 'alpha_asc' | 'alpha_desc'

export interface PinnedVarConfig {
  hideNull: boolean
  chartType: PinnedVarChartType
  sortOrder: PinnedVarSortOrder
}

export const DEFAULT_PINNED_CONFIG: PinnedVarConfig = {
  hideNull: false,
  chartType: 'bar',
  sortOrder: 'count_desc',
}

export interface PinnedVarBreakdown {
  key: string
  breakdown: VarValueBreakdownItem[]
  config: PinnedVarConfig
}

function looksLikeDateKey(key: string): boolean {
  const normalized = key.toLowerCase()
  return normalized === 'created_at'
    || normalized === 'updated_at'
    || normalized === 'time_created'
    || normalized === 'time_updated'
    || normalized.endsWith('_at')
    || normalized.includes('date')
    || normalized.includes('time')
}

function parseDateValue(raw: unknown): Date | null {
  if (raw == null) return null
  if (typeof raw === 'number') {
    // Only treat numbers that are plausibly epoch timestamps as dates. Small
    // integers like a "Household Size" of 5 are NOT dates — coercing them would
    // render "1970-01-01". Require seconds since ~2001 (>1e9) or ms since ~2001 (>1e12).
    const isEpochSeconds = raw > 1e9 && raw < 1e12
    const isEpochMillis = raw >= 1e12
    if (!isEpochSeconds && !isEpochMillis) return null
    const ms = isEpochMillis ? raw : raw * 1000
    const d = new Date(ms)
    return Number.isNaN(d.getTime()) ? null : d
  }
  if (typeof raw === 'string') {
    // Require date-ish structure so plain numeric strings ("5") or short codes
    // ("m") aren't accidentally parsed as dates by the lenient Date constructor.
    const s = raw.trim()
    if (!/[-/:]|\d{4}/.test(s) || /^\d+$/.test(s)) return null
    const d = new Date(s)
    return Number.isNaN(d.getTime()) ? null : d
  }
  return null
}

// Values can be objects/arrays (repeating-group form fields), which String() would
// flatten to "[object Object]". Label them meaningfully instead.
export function formatVarValue(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return '(none)'
    return `${value.length} item${value.length === 1 ? '' : 's'}`
  }
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>
    for (const k of ['name', 'title', 'label', 'value']) {
      const v = obj[k]
      if (typeof v === 'string' && v.trim()) return v
      if (typeof v === 'number') return String(v)
    }
    const keys = Object.keys(obj)
    if (keys.length === 0) return '(empty)'
    return `{${keys.slice(0, 3).join(', ')}${keys.length > 3 ? ', …' : ''}}`
  }
  return String(value)
}

function dateBucketLabel(date: Date, granularity: 'day' | 'hour'): string {
  const yyyy = date.getUTCFullYear()
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0')
  const dd = String(date.getUTCDate()).padStart(2, '0')
  if (granularity === 'day') return `${yyyy}-${mm}-${dd}`
  const hh = String(date.getUTCHours()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd} ${hh}:00`
}

function getNestedValue(source: unknown, path: string): unknown {
  if (!source || typeof source !== 'object' || !path) return undefined
  return path.split('.').reduce<unknown>((acc, part) => {
    if (!acc || typeof acc !== 'object') return undefined
    return (acc as Record<string, unknown>)[part]
  }, source)
}

function extractValuesFromVars(vars: Record<string, any> | undefined, key: string): unknown[] {
  if (!vars) return []
  if (key.startsWith('extractions.')) {
    const fieldKey = key.slice('extractions.'.length)
    const root = vars.extractions || vars.analysis_extractions
    if (!root || typeof root !== 'object') return []
    const out: unknown[] = []
    Object.values(root).forEach((payload: any) => {
      const data = payload && typeof payload === 'object'
        ? (payload.extracted_data && typeof payload.extracted_data === 'object' ? payload.extracted_data : payload)
        : null
      const nestedValue = getNestedValue(data, fieldKey)
      if (nestedValue === undefined) return
      if (nestedValue && typeof nestedValue === 'object' && !Array.isArray(nestedValue)) return
      if (Array.isArray(nestedValue)) {
        out.push(...nestedValue)
        return
      }
      out.push(nestedValue)
    })
    return out
  }

  const value = vars[key]
  if (value === undefined) return []
  if (value && typeof value === 'object' && !Array.isArray(value)) return []
  if (Array.isArray(value)) return value
  return [value]
}

// Pure function so it can be reused outside the hook
export function computeVarBreakdown(
  key: string,
  filteredContacts: RawContact[],
  filteredMessages: RawMessage[]
): VarValueBreakdownItem[] {
  const values: Record<string, number> = {}
  const dateCandidates: Date[] = []
  const candidateValues: unknown[] = []

  filteredContacts.forEach(c => {
    const valuesForKey = extractValuesFromVars(c.vars, key)
    valuesForKey.forEach((v) => {
      candidateValues.push(v)
      const parsed = parseDateValue(v)
      if (parsed) dateCandidates.push(parsed)
    })
  })

  if (candidateValues.length === 0) {
    filteredMessages.forEach(m => {
      const valuesForKey = extractValuesFromVars(m.vars, key)
      valuesForKey.forEach((v) => {
        candidateValues.push(v)
        const parsed = parseDateValue(v)
        if (parsed) dateCandidates.push(parsed)
      })
    })
  }

  const shouldBucketAsDate =
    looksLikeDateKey(key)
    || (candidateValues.length > 0 && dateCandidates.length / candidateValues.length >= 0.8)

  let granularity: 'day' | 'hour' = 'day'
  if (dateCandidates.length > 0) {
    const uniqueDays = new Set(dateCandidates.map(d => dateBucketLabel(d, 'day')))
    if (uniqueDays.size === 1) granularity = 'hour'
  }

  candidateValues.forEach((value) => {
    if (value === undefined) return
    if (shouldBucketAsDate) {
      const parsed = parseDateValue(value)
      const bucket = parsed ? dateBucketLabel(parsed, granularity) : formatVarValue(value)
      values[bucket] = (values[bucket] || 0) + 1
      return
    }
    const val = formatVarValue(value)
    values[val] = (values[val] || 0) + 1
  })

  return Object.entries(values)
    .map(([value, count], index) => ({
      value: value || '(empty)',
      count,
      color: CHART_COLORS[index % CHART_COLORS.length],
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 20)
}

// Hook for computing breakdowns for multiple pinned variable keys at once
export function usePinnedVarsBreakdowns(
  filteredContacts: RawContact[],
  filteredMessages: RawMessage[],
  pinnedKeys: string[],
  pinnedConfigs: Record<string, PinnedVarConfig>
): PinnedVarBreakdown[] {
  return useMemo(() => {
    if (!pinnedKeys.length) return []
    return pinnedKeys.map(key => {
      const config = pinnedConfigs[key] ?? DEFAULT_PINNED_CONFIG
      const raw = computeVarBreakdown(key, filteredContacts, filteredMessages)

      // Apply null filter
      const filtered = config.hideNull
        ? raw.filter(item => item.value !== 'null' && item.value !== 'NULL' && item.value !== '(empty)')
        : raw

      // Apply sort
      const breakdown = [...filtered].sort((a, b) => {
        switch (config.sortOrder) {
          case 'count_asc':  return a.count - b.count
          case 'alpha_asc':  return a.value.localeCompare(b.value)
          case 'alpha_desc': return b.value.localeCompare(a.value)
          case 'count_desc':
          default:           return b.count - a.count
        }
      })

      return { key, breakdown, config }
    })
  }, [pinnedKeys, pinnedConfigs, filteredContacts, filteredMessages])
}

export function useVariablesAnalytics(
  filteredContacts: RawContact[],
  filteredMessages: RawMessage[],
  selectedVarKey: string,
  selectedVarValue: string
) {
  // Variable value breakdown for selected var
  const varValueBreakdown = useMemo((): VarValueBreakdownItem[] => {
    if (!selectedVarKey) return []
    return computeVarBreakdown(selectedVarKey, filteredContacts, filteredMessages)
  }, [selectedVarKey, filteredContacts, filteredMessages])

  // Contacts with selected variable value
  const contactsWithVarValue = useMemo(() => {
    if (!selectedVarKey || !selectedVarValue) return []
    
    return filteredContacts
      .filter(c => {
        const valuesForKey = extractValuesFromVars(c.vars, selectedVarKey)
        if (valuesForKey.length === 0) return false
        return valuesForKey.some((value) => {
          const val = formatVarValue(value)
          return val === selectedVarValue || (selectedVarValue === '(empty)' && !val)
        })
      })
      .sort((a, b) => (b.last_message_time || 0) - (a.last_message_time || 0))
  }, [selectedVarKey, selectedVarValue, filteredContacts])

  return {
    varValueBreakdown,
    contactsWithVarValue
  }
}
